import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ReportShell } from "../ReportShell";
import {
  Banner,
  Card,
  Delta,
  Findings,
  Kpi,
  Kpis,
  MetricCell,
  ProgressCell,
  RowLabel,
  Sparkline,
  TableCard,
  fmt,
  type Col,
  type FmtKind,
} from "../ds";
import { ErrorBox, State } from "../Panels";
import { useReport, type ReportResponse } from "../useReport";
import { Spark, TrendModal, type TrendSpec } from "../ceo/Trend";
import { ChartLegend, DualChart } from "./Chart";
import { Drill } from "./Drill";
import {
  ACTION_RULES,
  ALERT_LEGEND,
  CAMPAIGN_TYPES,
  TYPE_ORDER,
  byVerdict,
  dayOf,
  daypartOf,
  judge,
  periodLabel,
  periodOf,
  ratio,
  roasColour,
  toneOf,
  type KeywordRow,
  type MktDay,
  type MktPeriod,
  type MktWindow,
  type ProductRow,
  type SovKeyword,
  type StructureRow,
  type VisibilityOverview,
} from "./data";
import { loadActiveProfile } from "@/lib/auth";
import { DEFAULT_RANGE, type DateRange } from "@/lib/dateRange";
import type { Route } from "@/lib/route";

const TITLES: Record<string, string> = {
  overview: "Overview",
  actions: "Actions",
  "spend-structure": "Spend structure",
  "keywords-sov": "Keywords & SOV",
  "timing-budget": "Timing & budget",
  skus: "SKUs",
};

/* ── the queries ──────────────────────────────────────────────────────────
 * Every section reads the overview, because the page banner's ROAS basis and
 * several cards' true-spend reconciliation come from it. Identical arguments
 * mean identical query keys, so react-query serves all six sections from one
 * cache entry rather than six requests.
 */
const OVERVIEW = "/v1/reports/marketing/overview";

/** 500 is the endpoint's cap. The drill is meant to show every keyword. */
const KW_ARGS = { limit: 500 };
/**
 * Share of voice reuses the Visibility endpoint rather than growing a second
 * SOV query: there are already two different SOVs on this platform and a third
 * implementation of the Hector-derived one is how two pages start quietly
 * disagreeing about the same brand in the same window. `products: 0` drops a
 * query this page does not read.
 */
const SOV_ARGS = { filters: {}, limit: 50, products: 0 };

/** What a section needs of a query: is it in flight, did it fail, and why. */
type Q = UseQueryResult<ReportResponse, Error>;

function useSov(
  customer: string,
  range: DateRange,
): {
  q: Q;
  data: VisibilityOverview | null;
} {
  const q = useReport("/v1/visibility/overview", customer, range, SOV_ARGS);
  // The shared ReportResponse shape covers the /reports/* pages; Visibility has
  // its own envelope (kpis / keywords / coverage) and this is where the two meet.
  const data = q.data ? (q.data as unknown as VisibilityOverview) : null;
  return { q, data };
}

export function MarketingView({
  route,
  onNavigate,
  onSignOut,
}: {
  route: Route;
  onNavigate: (s: string, p: string) => void;
  onSignOut?: () => void;
}) {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const customer = loadActiveProfile() ?? "";
  const page = route.page || "overview";
  const anchor = useReport(OVERVIEW, customer, range);

  return (
    <ReportShell
      title={`Marketing — ${TITLES[page] ?? "Overview"}`}
      route={{ section: "marketing", page }}
      onNavigate={onNavigate}
      range={range}
      onRangeChange={setRange}
      {...(anchor.data?.window ? { resolved: anchor.data.window } : {})}
      {...(onSignOut ? { onSignOut } : {})}
    >
      {page === "spend-structure" && (
        <SpendStructure customer={customer} range={range} />
      )}
      {page === "keywords-sov" && (
        <KeywordsSov customer={customer} range={range} />
      )}
      {/* Spend/ROAS plan vs actual — Overview + SKUs only (ticket 14zkq6bch7j) */}
      {(page === "overview" || !TITLES[page] || page === "skus") && (
        <SpendPlanCard customer={customer} range={range} />
      )}
      {page === "skus" && <Skus customer={customer} range={range} />}
      {page === "actions" && <Actions customer={customer} range={range} />}
      {page === "timing-budget" && <Timing customer={customer} range={range} />}
      {(page === "overview" || !TITLES[page]) && (
        <Overview customer={customer} range={range} />
      )}
    </ReportShell>
  );
}

/* ── the page banner ─────────────────────────────────────────────────────── */

/**
 * The basis strip the published page puts above EVERY section.
 *
 * Three jobs, all of them load-bearing: it names the day the page runs to, it
 * names the ROAS basis (ad-attributed sales are not the GMV basis the
 * Operations and CEO pages report, and nothing else on the page says so), and
 * it warns when the comparison period the server chose does not exist — a
 * delta against days that were never crawled reads as a collapse.
 *
 * The report's version of the ROAS note was a frozen string that sat in the
 * header quoting three-week-old figures. This one is recomputed per window by
 * the server.
 */
function PageBanner({
  win,
  roas,
  sov,
}: {
  win: MktWindow | undefined;
  roas?: string | undefined;
  /** The SOV feed's own window, on the sections that read it. */
  sov?: MktWindow | null;
}) {
  if (!win) return null;
  const feeds = sov ? 2 : 1;
  return (
    <Banner>
      <b>Everything on this page runs to {win.as_of}.</b>{" "}
      {feeds === 2 ? (
        <>
          Two feeds sit behind it and they settle at different times, so the
          page ends where the slowest one does — advertising (to {win.as_of})
          and share of voice (to {sov?.as_of ?? "—"}). Later days exist in the
          fresher feed but are held back, because two figures covering different
          windows should never sit side by side.{" "}
        </>
      ) : (
        <>
          The advertising feed sits behind it and settles a day or two after the
          fact, so the page ends where that feed does rather than at today.{" "}
        </>
      )}
      Figures that cannot follow the date picker at all are tagged{" "}
      <i>fixed window</i>.
      {roas ? (
        <>
          {" "}
          <b>ROAS basis:</b> {roas}
        </>
      ) : null}
      {win.comparison_available === false ? (
        <>
          <br />
          <b>No comparison available:</b>{" "}
          {periodLabel(win.previous_start, win.previous_end)} falls outside what
          this feed holds, so no delta is shown rather than one computed over
          days that do not exist.
        </>
      ) : null}
      {win.note ? (
        <>
          <br />
          {win.note}
        </>
      ) : null}
    </Banner>
  );
}

/* ── shared cell builders ────────────────────────────────────────────────── */

/** The report's banded ROAS figure: green at 3x, red under 1.5x, ink between. */
function Roas({ v }: { v: number | null }) {
  return (
    <span style={{ color: roasColour(v), fontWeight: 600 }}>
      {fmt(v, "ratio")}
    </span>
  );
}

/** An italic grey caption for a figure the feed does not carry for this row. */
function Muted({ children }: { children: ReactNode }) {
  return <span className="stat-val muted">{children}</span>;
}

function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>;
}

function ViewAll({ n, onClick }: { n: number; onClick: () => void }) {
  return (
    <button className="viewall" onClick={onClick}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <path d="M1.5 3h9M1.5 6h9M1.5 9h5" />
      </svg>
      View all {n}
    </button>
  );
}

/* ── 1. Overview ─────────────────────────────────────────────────────────── */

interface Tile {
  label: string;
  kind: FmtKind;
  good: "up" | "down" | "neutral";
  value: (p: MktPeriod) => number | null;
  /**
   * The per-day value for the sparkline, or null when the daily series does not
   * carry what the metric needs. An empty sparkline is drawn rather than a
   * reconstructed one.
   */
  daily: ((d: MktDay) => number | null) | null;
  formula: string;
}

/**
 * The six KPI tiles, in the published page's order.
 *
 * The headline ratios are POOLED — a ratio of sums — while each sparkline plots
 * that day's own ratio. The two therefore sit on different bases by design: a
 * mean of the daily points will not equal the tile. Averaging daily ROAS would
 * let a ₹200 day count as much as a ₹200,000 one.
 */
const TILES: Tile[] = [
  {
    label: "SPEND",
    kind: "money",
    good: "neutral",
    value: (p) => p.spend,
    daily: (d) => d.spend,
    formula: "SUM(spend)",
  },
  {
    label: "AD SALES",
    kind: "money",
    good: "up",
    value: (p) => p.ad_sales,
    daily: (d) => d.ad_sales,
    formula: "SUM(direct_sales + indirect_sales) — attributed, not incremental",
  },
  {
    label: "ROAS",
    kind: "ratio",
    good: "up",
    value: (p) => p.roas,
    daily: (d) => d.roas,
    formula:
      "SUM(direct_sales + indirect_sales) / SUM(spend) — never an average of daily ROAS",
  },
  {
    label: "ACOS",
    kind: "pct",
    good: "down",
    value: (p) => p.acos,
    // Re-derived per day from the two figures the series does carry, which is
    // the same arithmetic the pooled figure uses, not an average of ratios.
    daily: (d) => {
      const r = ratio(d.spend, d.ad_sales);
      return r === null ? null : r * 100;
    },
    formula: "SUM(spend) / SUM(direct_sales + indirect_sales) x 100",
  },
  {
    label: "COST PER ATC",
    kind: "price",
    good: "down",
    value: (p) => p.cost_per_atc,
    daily: null,
    formula: "SUM(spend) / SUM(direct_atc + indirect_atc)",
  },
  {
    label: "NEW CUSTOMERS",
    kind: "int",
    good: "up",
    value: (p) => p.new_customers,
    daily: null,
    formula: "SUM(new_users)",
  },
];

function Overview({ customer, range }: { customer: string; range: DateRange }) {
  const q = useReport(OVERVIEW, customer, range);
  const kw = useReport<KeywordRow>(
    "/v1/reports/marketing/keywords",
    customer,
    range,
    KW_ARGS,
  );
  const sov = useSov(customer, range);
  const [trendOpen, setTrendOpen] = useState<TrendSpec | null>(null);
  const openTrend = useCallback((spec: TrendSpec) => {
    setTrendOpen(spec);
  }, []);

  const days: MktDay[] = useMemo(
    () => (q.data?.series ?? []).map(dayOf),
    [q.data],
  );
  const cur = periodOf(q.data?.periods, "current");
  const prev = periodOf(q.data?.periods, "previous");
  const win: MktWindow | undefined = q.data?.window;

  if (q.isLoading) return <State>Loading marketing…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;
  if (!cur) {
    return (
      <>
        <PageBanner
          win={win}
          {...(q.data?.roas_basis?.note
            ? { roas: q.data.roas_basis.note }
            : {})}
        />
        <State>No advertising data for this brand in this window.</State>
      </>
    );
  }

  const compared = prev !== null && win?.comparison_available !== false;

  return (
    <>
      <PageBanner
        win={win}
        {...(q.data?.roas_basis?.note ? { roas: q.data.roas_basis.note } : {})}
        sov={sov.data?.window ?? null}
      />
      <Kpis>
        {TILES.map((t) => {
          const c = t.value(cur);
          const p = compared && prev ? t.value(prev) : null;
          const series = t.daily ? days.map(t.daily) : [];
          const dates = days.map((d) => d.day);
          const spec: TrendSpec = {
            label: t.label,
            dates,
            values: series,
            kind: t.kind,
            subtitle: t.formula,
            formula: t.formula,
          };
          return (
            <Kpi
              key={t.label}
              label={t.label}
              title={t.formula}
              value={fmt(c, t.kind)}
              {...(compared
                ? { delta: <Delta cur={c} prev={p} good={t.good} /> }
                : {})}
              spark={
                series.length >= 2 ? (
                  <Spark
                    spec={spec}
                    tone={toneOf(c, p, t.good)}
                    w={150}
                    h={26}
                    onOpen={openTrend}
                  />
                ) : (
                  <Sparkline
                    values={series}
                    tone={toneOf(c, p, t.good)}
                    w={150}
                    h={26}
                  />
                )
              }
              foot={
                compared
                  ? `vs ${periodLabel(win?.previous_start, win?.previous_end)}`
                  : "no comparison data"
              }
            />
          );
        })}
      </Kpis>
      <FindingsCard
        win={win}
        cur={cur}
        prev={compared ? prev : null}
        keywords={kw.data?.rows ?? []}
        sov={sov.data}
      />
      {q.data?.roas_basis ? <RoasBasis b={q.data.roas_basis} /> : null}
      {trendOpen ? (
        <TrendModal
          spec={trendOpen}
          horizon={win?.as_of ?? "—"}
          onClose={() => {
            setTrendOpen(null);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * WHICH BASIS THIS ROAS IS ON.
 *
 * Kept from the previous in-app version and restyled to the report's own
 * components. The published page carries this as prose in its header, and that
 * prose went stale — it quoted 1–19 Aug figures for three weeks while every
 * number beside it moved. These are the two measured per-unit figures instead,
 * recomputed per window by the server.
 */
function RoasBasis({ b }: { b: NonNullable<ReportResponse["roas_basis"]> }) {
  if (b.ad_per_unit === null || b.portal_per_unit === null) return null;
  const gap = b.ratio === null ? null : (b.ratio - 1) * 100;
  return (
    <Card
      title="What ROAS is measured on"
      {...(b.campaigns === null
        ? {}
        : { scope: `${fmt(b.campaigns, "int")} campaigns in this window` })}
      foot={b.note}
    >
      <div className="panel-body">
        <Kpis>
          <Kpi
            label="AD SALES PER UNIT"
            value={fmt(b.ad_per_unit, "price")}
            foot={`${fmt(b.direct_sales, "money")} direct ÷ ${fmt(b.direct_qty, "int")} units`}
          />
          <Kpi
            label="PORTAL SALES PER UNIT"
            value={fmt(b.portal_per_unit, "price")}
            foot={`${b.portal_basis.toUpperCase()} basis · ${fmt(b.portal_units, "int")} units sold`}
          />
          <Kpi
            label="GAP"
            value={
              gap === null ? "—" : `${gap >= 0 ? "+" : ""}${gap.toFixed(1)}%`
            }
            foot="ad-attributed vs measured"
          />
        </Kpis>
      </div>
    </Card>
  );
}

/**
 * "What this means" — the findings card.
 *
 * Five rules on the published page, computed live from the page's own figures
 * each time the period changes. Three of them can run here. The two that cannot
 * are NAMED in the disclaimer rather than left out silently, because a findings
 * card that quietly drops a rule reads as "nothing to report".
 */
function FindingsCard({
  win,
  cur,
  prev,
  keywords,
  sov,
}: {
  win: MktWindow | undefined;
  cur: MktPeriod;
  prev: MktPeriod | null;
  keywords: KeywordRow[];
  sov: VisibilityOverview | null;
}) {
  const items: Array<{ key: string; text: ReactNode; meta: ReactNode }> = [];

  // roas_move — pooled ROAS now against pooled ROAS then, with the spend move
  // beside it, because a ROAS that improved on collapsing spend is not a win.
  if (prev) {
    const now = cur.roas;
    const was = prev.roas;
    if (now !== null && was !== null && Math.abs(now - was) >= 0.08) {
      const spendMove = prev.spend
        ? (((cur.spend ?? 0) - prev.spend) / Math.abs(prev.spend)) * 100
        : null;
      items.push({
        key: "roas_move",
        text: (
          <>
            Return on ad spend {now < was ? "fell" : "rose"} from{" "}
            {fmt(was, "ratio")} to <b>{fmt(now, "ratio")}</b>
            {spendMove === null ? (
              "."
            ) : (
              <>
                {" "}
                while spend {spendMove >= 0 ? "rose" : "fell"}{" "}
                {Math.abs(spendMove).toFixed(1)}%.
              </>
            )}
          </>
        ),
        meta: `sales ${fmt(cur.ad_sales, "money")} / spend ${fmt(cur.spend, "money")} · vs ${periodLabel(win?.previous_start, win?.previous_end)}`,
      });
    }
  }

  // rented_visibility — ad share with no organic share behind it. The single
  // most expensive thing on this page to not know.
  const rented = (sov?.keywords ?? [])
    .map((k) => ({ k, ad: k.ad_sov.value, org: k.organic_sov.value }))
    .filter(
      (x): x is { k: SovKeyword; ad: number; org: number } =>
        x.ad !== null && x.org !== null && x.org < 1 && x.ad > 15,
    )
    .sort((a, b) => b.ad - a.ad);
  const top = rented[0];
  if (top) {
    items.push({
      key: "rented_visibility",
      text: (
        <>
          On {rented.map((r) => r.k.label).join(", ")} the brand holds ad share
          but almost no organic share —{" "}
          <b>
            {top.k.label} runs {top.ad.toFixed(1)}% ad SOV against{" "}
            {top.org.toFixed(2)}% organic
          </b>
          . That visibility is rented, and disappears when the campaign stops.
        </>
      ),
      meta: `pooled ${sov?.window.start ?? "—"}→${sov?.window.end ?? "—"} · ${fmt(top.k.appearances, "int")} appearances · ${sov?.sov_source ?? "hector_derived"}`,
    });
  }

  // weak_generics — the three biggest keywords under the ROAS floor. Revenue
  // share is summed from the endpoint's own full-period denominator, so it is
  // a true share of all keyword revenue; the spend share is not (see below).
  const worst = keywords
    .filter((r) => (r.roas ?? 0) < ACTION_RULES.roas_weak && (r.spend ?? 0) > 0)
    .sort((a, b) => (b.spend ?? 0) - (a.spend ?? 0))
    .slice(0, 3);
  if (worst.length) {
    const allSpend = keywords.reduce((a, r) => a + (r.spend ?? 0), 0);
    const sp = worst.reduce((a, r) => a + (r.spend ?? 0), 0);
    const rev = worst.reduce((a, r) => a + (r.ad_sales ?? 0), 0);
    const revShare = worst.reduce((a, r) => a + (r.revenue_share ?? 0), 0);
    items.push({
      key: "weak_generics",
      text: (
        <>
          {worst.map((r) => r.keyword).join(", ")} take{" "}
          <b>
            {allSpend
              ? `${((sp / allSpend) * 100).toFixed(1)}%`
              : "an unknown share"}{" "}
            of keyword spend at a combined {fmt(ratio(rev, sp), "ratio")} return
          </b>
          , delivering {fmt(revShare, "pct")} of keyword revenue.
        </>
      ),
      meta: `${win?.start ?? "—"}→${win?.end ?? "—"} · spend ${fmt(sp, "money")} of ${fmt(allSpend, "money")} across the ${keywords.length} keywords returned`,
    });
  }

  return (
    <Findings
      title="What this means"
      items={items}
      disclaimer={
        <>
          Computed live from this page&rsquo;s own data each time the period
          changes — not a maintained list. Each finding shows the figures behind
          it.{" "}
          <b>Two of the published report&rsquo;s five rules cannot run here:</b>{" "}
          budget pacing needs the campaign alert feed and day-parting spend, and
          the funnel-shift rule needs previous-period figures per campaign type.
          Neither is served by the API yet, so they are named rather than left
          to read as &ldquo;nothing found&rdquo;.
        </>
      }
    />
  );
}

/* ── 2. Actions — scale / optimise / stop ────────────────────────────────── */

function Actions({ customer, range }: { customer: string; range: DateRange }) {
  const ov = useReport(OVERVIEW, customer, range);
  const q = useReport<KeywordRow>(
    "/v1/reports/marketing/keywords",
    customer,
    range,
    KW_ARGS,
  );
  const sov = useSov(customer, range);
  const win: MktWindow | undefined = ov.data?.window;

  const judged = useMemo(() => {
    const rows = q.data?.rows ?? [];
    const total = rows.reduce((a, r) => a + (r.spend ?? 0), 0);
    // Keyed on the lowercased keyword: the ads table and the crawl spell the
    // same term with different case, and a case-sensitive join silently drops
    // the organic column for exactly the rows that matter most.
    const organic = new Map<string, number | null>(
      (sov.data?.keywords ?? []).map((k) => [
        k.label.toLowerCase(),
        k.organic_sov.value,
      ]),
    );
    return rows
      .map((r) =>
        judge(
          r.keyword,
          r.match_type,
          r.spend ?? 0,
          r.roas,
          total ? (r.spend ?? 0) / total : null,
          organic.get(r.keyword.toLowerCase()) ?? null,
        ),
      )
      .sort(byVerdict);
  }, [q.data, sov.data]);

  if (q.isLoading) return <State>Loading keywords…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;
  if (judged.length === 0) {
    return (
      <>
        <PageBanner
          win={win}
          {...(ov.data?.roas_basis?.note
            ? { roas: ov.data.roas_basis.note }
            : {})}
          sov={sov.data?.window ?? null}
        />
        <State>No keyword spend to act on in this window.</State>
      </>
    );
  }

  const stop = judged.filter((x) => x.action === "STOP");
  const scale = judged.filter((x) => x.action === "SCALE");
  const atRisk = stop.reduce((a, x) => a + x.spend, 0);
  const total = judged.reduce((a, x) => a + x.spend, 0);
  const cols: Col[] = [
    { label: "Verdict" },
    { label: "Keyword" },
    { label: "Spend", type: "num" },
    {
      label: "ROAS",
      type: "num",
      title: "SUM(direct_sales + indirect_sales) / SUM(spend)",
    },
    {
      label: "Organic SOV",
      type: "num",
      title: "Pooled organic share of voice for the selected period",
    },
    { label: "Why" },
  ];

  return (
    <>
      <PageBanner
        win={win}
        {...(ov.data?.roas_basis?.note
          ? { roas: ov.data.roas_basis.note }
          : {})}
        sov={sov.data?.window ?? null}
      />
      <TableCard
        title="Scale / optimise / stop"
        scope={`${win?.start ?? "—"} to ${win?.end ?? "—"}`}
        asof={`${stop.length} stop · ${scale.length} scale`}
        cols={cols}
        rows={judged.map((x) => ({
          key: x.keyword,
          cells: [
            <span className={`verdict ${x.action.toLowerCase()}`}>
              {x.action}
            </span>,
            <RowLabel
              name={x.keyword}
              sub={
                x.match_type ? (
                  <Tag>{x.match_type}</Tag>
                ) : (
                  <Muted>match type not given</Muted>
                )
              }
            />,
            <MetricCell
              value={fmt(x.spend, "money")}
              delta={
                <div className="delta flat">
                  {x.share === null
                    ? "share unknown"
                    : `${(x.share * 100).toFixed(1)}% of spend`}
                </div>
              }
            />,
            <Roas v={x.roas} />,
            x.organic === null ? (
              <Muted>not crawled</Muted>
            ) : (
              fmt(x.organic, "pct2")
            ),
            <span style={{ color: "var(--ink-2)", fontSize: "12.5px" }}>
              {x.why}
            </span>,
          ],
        }))}
        foot={
          <>
            <b>{fmt(atRisk, "money")}</b> of keyword spend sits on STOP verdicts
            — {total ? `${((atRisk / total) * 100).toFixed(1)}%` : "—"} of the
            keyword budget the endpoint returned. Verdicts are computed, not
            written: SCALE = ROAS ≥ {ACTION_RULES.roas_strong}x on under 10% of
            spend · STOP = ROAS &lt; {ACTION_RULES.roas_weak}x · OPTIMISE = the
            rest, flagged where organic SOV is under {ACTION_RULES.organic_thin}
            %. Thresholds live in one block in the code and are Hector&rsquo;s
            to set, not the platform&rsquo;s. ROAS is attributed — a brand
            keyword scores high because it captures demand that already existed,
            so SCALE on a brand term means <b>defend, not expand</b>. Share of
            spend is against the {judged.length} keywords this window returned,
            not against every keyword group in it: the endpoint does not send a
            full-set spend total, so a keyword beyond the row cap would be
            missing from that denominator. Organic SOV is the crawl&rsquo;s
            pooled figure for the same window and covers only the keywords under
            crawl — the rest read <i>not crawled</i> rather than zero.
          </>
        }
      />
    </>
  );
}

/* ── 2b. Spend & ROAS plan vs actual (Change 5) ─────────────────────────── */

/**
 * Spend and ROAS plan vs actual — same numbers as the Control tower,
 * placed on the Marketing Overview per spec §5.4B.
 */
function SpendPlanCard({
  customer,
  range,
}: {
  customer: string;
  range: DateRange;
}) {
  const q = useReport("/v1/reports/ops/plan-actual", customer, range);
  const mkt = useReport(OVERVIEW, customer, range);

  if (!q.data || !mkt.data) return null;

  const n = (v: unknown): number | null => {
    const x = typeof v === "number" ? v : Number(v);
    return Number.isFinite(x) ? x : null;
  };

  const totals = q.data.totals;
  const planSpend = n(totals?.["plan_spend"]);
  const planRoas = n(totals?.["plan_roas"]);
  if (!planSpend || planSpend <= 0) return null;

  const cur = periodOf(mkt.data.periods, "current");
  const actualSpend = n(cur?.["spend"]) ?? 0;
  const actualRoas = n(cur?.["roas"]) ?? null;
  const spendVsPlan = planSpend > 0 ? (actualSpend / planSpend) * 100 : null;
  const win = q.data.window;

  return (
    <Card
      title="Spend & ROAS — plan vs actual"
      scope={periodLabel(win.start, win.end)}
      asof={`Plan on MRP · data to ${win.as_of}`}
      foot={
        <>
          Spend plan and ROAS target from the client&rsquo;s workbook. Actual
          spend and ROAS from the advertising feed. <b>Same numbers</b> as the
          Control tower. ROAS target is never pro-rated &mdash; it is a ratio.
        </>
      }
    >
      <div style={{ padding: "0 18px" }}>
        <Kpis>
          <Kpi
            label="Spend plan"
            value={fmt(planSpend, "money")}
            foot="from the workbook"
          />
          <Kpi
            label="Spend actual"
            value={fmt(actualSpend, "money")}
            foot="advertising feed"
          />
          <Kpi
            label="Spend vs plan"
            value={
              spendVsPlan !== null ? (
                <span
                  style={{
                    color:
                      spendVsPlan > 110
                        ? "var(--down)"
                        : spendVsPlan < 90
                          ? "var(--warn)"
                          : "var(--up)",
                  }}
                >
                  {spendVsPlan.toFixed(1)}%
                </span>
              ) : (
                "—"
              )
            }
            foot="actual ÷ plan"
          />
          <Kpi
            label="ROAS target"
            value={planRoas !== null ? `${planRoas.toFixed(1)}x` : "—"}
            foot="never pro-rated"
          />
          <Kpi
            label="ROAS actual"
            value={
              actualRoas !== null ? (
                <span style={{ color: roasColour(actualRoas) }}>
                  {actualRoas.toFixed(2)}x
                </span>
              ) : (
                "—"
              )
            }
            foot="ad-attributed sales ÷ spend"
          />
        </Kpis>
      </div>
    </Card>
  );
}

/* ── 3. Spend structure — by campaign type ───────────────────────────────── */

function SpendStructure({
  customer,
  range,
}: {
  customer: string;
  range: DateRange;
}) {
  const ov = useReport(OVERVIEW, customer, range);
  const q = useReport<StructureRow>(
    "/v1/reports/marketing/spend-structure",
    customer,
    range,
  );
  const win: MktWindow | undefined = ov.data?.window;
  const cur = periodOf(ov.data?.periods, "current");
  const prev = periodOf(ov.data?.periods, "previous");
  const compared = prev !== null && win?.comparison_available !== false;

  if (q.isLoading) return <State>Loading spend structure…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <>
        <PageBanner
          win={win}
          {...(ov.data?.roas_basis?.note
            ? { roas: ov.data.roas_basis.note }
            : {})}
        />
        <State>No spend in this window.</State>
      </>
    );
  }

  // The published page's order — down the funnel, then anything it does not
  // name. Never by spend: the reader is comparing the same row to last month.
  const ordered = [...rows].sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a.campaign_type);
    const bi = TYPE_ORDER.indexOf(b.campaign_type);
    if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    return (b.spend ?? 0) - (a.spend ?? 0);
  });

  const cols: Col[] = [
    { label: "Campaign type" },
    {
      label: "Spend share",
      type: "num",
      title: "this type's spend / all spend in the window",
    },
    { label: "Spend", type: "num", title: "SUM(spend)" },
    {
      label: "ROAS",
      type: "num",
      title:
        "SUM(direct_sales + indirect_sales) / SUM(spend) — never an average of daily ROAS",
    },
    {
      label: "ACOS",
      type: "num",
      title: "SUM(spend) / SUM(direct_sales + indirect_sales) x 100",
    },
    {
      label: "ATC rate",
      type: "num",
      title:
        "SUM(direct_atc + indirect_atc) / SUM(unique_clicks) x 100 — the clicks basis, not the impressions basis the published report uses",
    },
    { label: "New customers", type: "num", title: "SUM(new_users)" },
    {
      label: "CPM",
      type: "num",
      title: "SUM(spend) / SUM(impressions) x 1000",
    },
  ];

  /**
   * A metric cell for a per-type row: value, then the delta, then the spark.
   *
   * Both of the last two are empty here and that is the honest rendering —
   * /spend-structure returns one aggregate per type for the current window
   * only, so there is no previous-period figure to compare against and no daily
   * series to draw. The components say "no comparison" and draw nothing rather
   * than showing a 0% that cannot be told from a measured one.
   */
  const cell = (v: number | null, kind: FmtKind): ReactNode => (
    <MetricCell
      value={fmt(v, kind)}
      delta={<Delta cur={v} prev={null} />}
      spark={<Sparkline values={[]} />}
    />
  );

  const totalRow = cur
    ? [
        {
          key: "__total",
          cls: "is-total",
          cells: [
            "All campaign types",
            <MetricCell value="100.0%" />,
            <MetricCell
              value={fmt(cur.spend, "money")}
              delta={
                <Delta
                  cur={cur.spend}
                  prev={compared ? (prev?.spend ?? null) : null}
                  good="neutral"
                />
              }
            />,
            <MetricCell
              value={fmt(cur.roas, "ratio")}
              delta={
                <Delta
                  cur={cur.roas}
                  prev={compared ? (prev?.roas ?? null) : null}
                  good="up"
                />
              }
            />,
            <MetricCell
              value={fmt(cur.acos, "pct")}
              delta={
                <Delta
                  cur={cur.acos}
                  prev={compared ? (prev?.acos ?? null) : null}
                  good="down"
                />
              }
            />,
            <MetricCell
              value={fmt(
                cur.clicks ? ((cur.atc ?? 0) / cur.clicks) * 100 : null,
                "pct",
              )}
              delta={
                <Delta
                  cur={ratio(cur.atc, cur.clicks)}
                  prev={
                    compared
                      ? ratio(prev?.atc ?? null, prev?.clicks ?? null)
                      : null
                  }
                  good="up"
                />
              }
            />,
            <MetricCell
              value={fmt(cur.new_customers, "int")}
              delta={
                <Delta
                  cur={cur.new_customers}
                  prev={compared ? (prev?.new_customers ?? null) : null}
                  good="up"
                />
              }
            />,
            <MetricCell
              value={fmt(cur.cpm, "price")}
              delta={
                <Delta
                  cur={cur.cpm}
                  prev={compared ? (prev?.cpm ?? null) : null}
                  good="down"
                />
              }
            />,
          ],
        },
      ]
    : [];

  return (
    <>
      <PageBanner
        win={win}
        {...(ov.data?.roas_basis?.note
          ? { roas: ov.data.roas_basis.note }
          : {})}
      />
      <TableCard
        title="By campaign type"
        scope={periodLabel(win?.start, win?.end)}
        asof={`Data to ${win?.as_of ?? "—"}`}
        cols={cols}
        rows={[
          ...ordered.map((r) => {
            const meta = CAMPAIGN_TYPES[r.campaign_type];
            return {
              key: r.campaign_type,
              cells: [
                <RowLabel
                  name={meta?.label ?? r.campaign_type}
                  sub={meta?.stage ?? "campaign type the report does not name"}
                />,
                <MetricCell value={fmt(r.spend_share, "pct")} />,
                cell(r.spend, "money"),
                cell(r.roas, "ratio"),
                cell(r.acos, "pct"),
                cell(r.atc_rate, "pct"),
                cell(r.new_customers, "int"),
                cell(r.cpm, "price"),
              ],
            };
          }),
          ...totalRow,
        ]}
        foot={
          <>
            Every row is measured directly: the ads table is grouped by{" "}
            <code>campaign_type</code>, so nothing here is a residual and the
            shares reconcile to the window&rsquo;s total exactly. The totals row
            is the account&rsquo;s own pooled figures, which also cover
            campaigns carrying no type at all. ROAS is{" "}
            <code>SUM(sales) / SUM(spend)</code>, never an average of daily
            ratios. <b>Per-type deltas and sparklines are empty by design</b> —
            this endpoint returns one aggregate per type for the selected
            window, with no previous period and no daily series, so the only
            real comparison on this card is the totals row.{" "}
            <b>ATC rate here is on the clicks basis</b> (
            <code>SUM(atc) / SUM(clicks)</code>); the published report divides
            by impressions, and this endpoint does not return per-type
            impressions to re-derive it.
          </>
        }
      />
    </>
  );
}

/* ── 4. Keywords & SOV ───────────────────────────────────────────────────── */

const KW_COLS: Col[] = [
  { label: "Keyword" },
  {
    label: "Spend",
    type: "num",
    title: "SUM(spend); share is of the keywords this window returned",
  },
  {
    label: "ROAS",
    type: "num",
    title: "SUM(direct_sales + indirect_sales) / SUM(spend)",
  },
  {
    label: "Revenue share",
    type: "num",
    title: "this keyword's sales / all keyword sales in the period",
  },
  {
    label: "Cost per ATC",
    type: "num",
    title: "SUM(spend) / SUM(direct_atc + indirect_atc)",
  },
  {
    label: "Search volume",
    type: "num",
    title: "National last-30-day market search volume",
  },
];

/**
 * One keyword row, identical in the card and in the drill.
 *
 * Shared rather than written twice: the drill exists so a reader can check the
 * rows the card leaves out, which only works if the two read the same.
 */
function keywordCells(
  r: KeywordRow,
  totalSpend: number,
  volume: number | null,
): ReactNode[] {
  return [
    <RowLabel
      name={r.keyword}
      sub={
        r.match_type ? (
          <Tag>{r.match_type}</Tag>
        ) : (
          <Muted>match type not given</Muted>
        )
      }
    />,
    <MetricCell
      value={fmt(r.spend, "money")}
      delta={
        <div className="delta flat">
          {totalSpend
            ? `${(((r.spend ?? 0) / totalSpend) * 100).toFixed(1)}% of spend`
            : "share unknown"}
        </div>
      }
    />,
    <Roas v={r.roas} />,
    fmt(r.revenue_share, "pct"),
    fmt(r.cost_per_atc, "price"),
    volume === null ? <Muted>not tracked</Muted> : fmt(volume, "int"),
  ];
}

function KeywordsSov({
  customer,
  range,
}: {
  customer: string;
  range: DateRange;
}) {
  const ov = useReport(OVERVIEW, customer, range);
  const q = useReport<KeywordRow>(
    "/v1/reports/marketing/keywords",
    customer,
    range,
    KW_ARGS,
  );
  const sov = useSov(customer, range);
  const [drill, setDrill] = useState(false);
  const [query, setQuery] = useState("");
  const win: MktWindow | undefined = ov.data?.window;

  const rows = q.data?.rows ?? [];
  const totalSpend = rows.reduce((a, r) => a + (r.spend ?? 0), 0);
  /**
   * National demand per keyword, borrowed from the crawl.
   *
   * The published report reads MAX(search_volume) off the window-rolled ads
   * keyword table; this endpoint does not carry it, and the Visibility endpoint
   * does — for the keywords under crawl. So a crawled keyword shows its real
   * national volume and the rest read "not tracked", which is the report's own
   * branch for the same absence.
   */
  const volume = new Map<string, number | null>(
    (sov.data?.keywords ?? []).map((k) => [
      k.label.toLowerCase(),
      k.search_volume ?? null,
    ]),
  );
  const top = rows.slice(0, 5);
  const shown = query.trim()
    ? rows.filter((r) =>
        r.keyword.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : rows;

  if (q.isLoading) return <State>Loading keywords…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  return (
    <>
      <PageBanner
        win={win}
        {...(ov.data?.roas_basis?.note
          ? { roas: ov.data.roas_basis.note }
          : {})}
        sov={sov.data?.window ?? null}
      />
      <TableCard
        title="Keywords"
        scope={`${win?.start ?? "—"} to ${win?.end ?? "—"}`}
        asof={`Data to ${win?.as_of ?? "—"}`}
        actions={
          rows.length > top.length ? (
            <ViewAll
              n={rows.length}
              onClick={() => {
                setDrill(true);
              }}
            />
          ) : undefined
        }
        cols={KW_COLS}
        rows={top.map((r) => ({
          key: r.keyword,
          cells: keywordCells(
            r,
            totalSpend,
            volume.get(r.keyword.toLowerCase()) ?? null,
          ),
        }))}
        foot={
          <>
            {top.length === rows.length
              ? `All ${top.length}`
              : `Top ${top.length}`}{" "}
            by spend of {rows.length} keywords with spend in the period.{" "}
            <b>Revenue share</b> is computed against every keyword in the period
            by the server, not against the rows displayed, so it never
            overstates; <b>share of spend</b> is against the {rows.length} rows
            returned, because no full-set spend total is sent.{" "}
            <b>Matches the selected period</b> — unlike the published report,
            whose keyword table is a frozen 60-day rollup because keyword × day
            exceeds its row cap. The tag is the platform&rsquo;s match type: the
            report tags Brand / Generic there, and that classification lives
            only on the window-rolled ads keyword table, which this endpoint
            does not read. ROAS is attributed: a brand keyword captures demand
            that largely already existed.
          </>
        }
      />
      <BrandSovTable customer={customer} range={range} />
      <Sov q={sov.q} data={sov.data} adsAsOf={win?.as_of} />
      {drill ? (
        <Drill
          title="All keywords"
          count={`${shown.length} of ${rows.length} · ${win?.start ?? "—"} to ${win?.end ?? "—"}`}
          search={query}
          onSearchChange={setQuery}
          onClose={() => {
            setDrill(false);
            setQuery("");
          }}
          foot={
            <>
              <span>
                These {rows.length} rows account for {fmt(totalSpend, "money")}{" "}
                of keyword spend. Revenue share is of the full period total;
                share of spend is of these rows.
              </span>
              <span className="spacer" />
              <span>Data to {win?.as_of ?? "—"}</span>
            </>
          }
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {KW_COLS.map((c, i) => (
                    <th
                      key={c.label}
                      {...(i === 0 ? {} : c.type ? { className: c.type } : {})}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 ? (
                  <tr>
                    <td colSpan={KW_COLS.length}>
                      <div className="empty">
                        No keyword matches that search.
                      </div>
                    </td>
                  </tr>
                ) : (
                  shown.map((r) => (
                    <tr key={r.keyword}>
                      {keywordCells(
                        r,
                        totalSpend,
                        volume.get(r.keyword.toLowerCase()) ?? null,
                      ).map((cell, i) => (
                        <td
                          key={KW_COLS[i]?.label ?? i}
                          {...(i === 0
                            ? {}
                            : KW_COLS[i]?.type
                              ? { className: KW_COLS[i]?.type }
                              : {})}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Drill>
      ) : null}
    </>
  );
}

/**
 * Share of voice.
 *
 * Pooled from the numerator/denominator atoms by the server — SUM of weighted
 * slots held over SUM of weighted slots present — never an average of daily
 * percentages, because rank 1 is worth about 12× a rank-40 slot and a mean of
 * daily shares weights a thin crawl day as heavily as a full one.
 */
function Sov({
  q,
  data,
  adsAsOf,
}: {
  q: Q;
  data: VisibilityOverview | null;
  adsAsOf: string | undefined;
}) {
  if (q.isLoading) return <State>Loading share of voice…</State>;
  // A missing SOV panel must not take the keyword table down with it.
  if (q.isError)
    return <ErrorBox>Share of voice unavailable: {q.error.message}</ErrorBox>;
  if (!data) return null;

  const rows = data.keywords;
  const win = data.window;
  if (rows.length === 0) {
    return (
      <Card title="Share of voice" scope={periodLabel(win.start, win.end)}>
        <div className="empty">
          No crawl days in {periodLabel(win.start, win.end)}.
        </div>
      </Card>
    );
  }

  const bought = rows.filter(
    (r) =>
      r.organic_sov.value !== null &&
      r.organic_sov.value < 1 &&
      (r.ad_sov.value ?? 0) > 15,
  );
  const cov = data.response_meta.coverage;
  const compared = win.comparison_available !== false;

  const bar = (c: number | null, own?: boolean): ReactNode =>
    c === null ? (
      "—"
    ) : (
      <ProgressCell
        value={fmt(c, "pct")}
        pct={c}
        {...(own ? { tone: "up" as const } : {})}
      />
    );

  return (
    <TableCard
      title="Share of voice"
      scope={periodLabel(win.start, win.end)}
      asof={`Data to ${win.as_of}`}
      cols={[
        { label: "Keyword" },
        {
          label: "Overall SOV",
          type: "num",
          title:
            "SUM(sov_numerator) / SUM(sov_denominator) — rank-weighted, pooled over the selected days",
        },
        {
          // The published page names the comparison in the header ("vs same
          // dates last month") because its period tabs each carry their own
          // comparison rule. The app's window control has one rule — the
          // preceding block of equal length — so the header stays short and the
          // dates it resolved to go in the tooltip.
          label: compared ? "vs previous" : "vs —",
          type: "num",
          title: compared
            ? `The comparison the server chose for this window: ${periodLabel(win.previous_start, win.previous_end)}`
            : "No comparable period in the crawl archive yet",
        },
        {
          label: "Ad SOV",
          type: "num",
          title:
            "SUM(ad_sov_numerator) / SUM(ad_sov_denominator) — sponsored slots only",
        },
        {
          label: "Organic SOV",
          type: "num",
          title:
            "SUM(organic_sov_numerator) / SUM(organic_sov_denominator) — a different denominator from Ad SOV",
        },
        { label: "Daily", type: "center" },
      ]}
      rows={rows.map((r) => {
        const rentedRow =
          r.organic_sov.value !== null &&
          r.organic_sov.value < 1 &&
          (r.ad_sov.value ?? 0) > 15;
        return {
          key: r.id,
          cells: [
            <RowLabel
              name={
                <>
                  {r.label}
                  {rentedRow ? <Tag>bought</Tag> : null}
                </>
              }
              sub={`${fmt(r.appearances, "int")} appearances`}
            />,
            bar(r.overall_sov.value),
            compared ? (
              <Delta
                cur={r.overall_sov.value}
                prev={r.overall_sov.previous}
                pp
                good="up"
              />
            ) : (
              <div className="delta flat">no comparison</div>
            ),
            bar(r.ad_sov.value),
            bar(r.organic_sov.value, (r.organic_sov.value ?? 0) >= 1),
            // Per-keyword daily SOV is not in this response — the series it
            // carries is the account's, pooled across every keyword, so drawing
            // it on each row would put the same line against six different
            // numbers. An empty spark is the honest cell.
            <Sparkline values={[]} w={92} h={20} />,
          ],
        };
      })}
      foot={
        <>
          Pooled from the numerator/denominator atoms: SUM of weighted slots
          held over SUM of weighted slots present,{" "}
          <b>never an average of daily percentages</b> — rank 1 counts about 12×
          a rank-40 slot. The denominator covers every crawl of every keyword in
          scope, including the days the brand held no slot at all, so a keyword
          that dropped out entirely on some days is not quietly flattered.{" "}
          {bought.length ? (
            <>
              <b>{bought.map((r) => r.label).join(", ")}</b>{" "}
              {bought.length === 1 ? "holds" : "hold"} ad share with effectively
              no organic share — that visibility is rented and stops when spend
              stops.{" "}
            </>
          ) : null}
          Ad and Organic use <b>separate denominators</b>, so the three columns
          do not sum to Overall.{" "}
          {cov ? (
            <>
              Coverage: {cov.keywords_in_scope} keywords across{" "}
              {cov.cities_in_scope} cities, {cov.crawls} crawls on{" "}
              {cov.days_with_crawls} days.{" "}
            </>
          ) : null}
          <b>The per-keyword daily column is empty</b>: this endpoint returns
          one pooled figure per keyword plus an account-level daily series, and
          the account series is not this keyword&rsquo;s. This is the{" "}
          <b>Hector-derived</b> SOV — rank-weighted, daily-capable,
          city-capable. Blinkit also reports its own: blended, national,
          trailing 30 days. They are different measurements, not a discrepancy
          to reconcile. The crawl runs to {win.as_of} and advertising to{" "}
          {adsAsOf ?? "—"} — read side by side, never combined.
        </>
      }
    />
  );
}

/* ── 4b. Brand Category SOV table (Change 4) ────────────────────────────── */

/**
 * Brand-level Category SOV — every brand in O'cean's category with Organic,
 * Ad and Overall SOV, sorted by Overall SOV descending.
 *
 * Uses the same ceoBrandSovSql as the CEO Competition table, auto-resolving
 * the brand's primary L2 category server-side. Shows pp change vs the
 * comparison period.
 */
interface BrandSovRow {
  rank: number;
  brand: string;
  own: boolean;
  organic: number | null;
  ad: number | null;
  overall: number | null;
  prevOrganic: number | null;
  prevAd: number | null;
  prevOverall: number | null;
}

function BrandSovTable({
  customer,
  range,
}: {
  customer: string;
  range: DateRange;
}) {
  const q = useReport("/v1/reports/marketing/brand-sov", customer, range);

  if (q.isLoading) return <State>Loading brand SOV…</State>;
  if (q.isError)
    return <ErrorBox>Brand SOV unavailable: {q.error.message}</ErrorBox>;

  const data = q.data as ReportResponse & {
    own_brand: string | null;
    category: string | null;
    universe_name?: string;
    rows: Array<{
      period: string;
      brand: string;
      overall_sov: number | null;
      ad_sov: number | null;
      organic_sov: number | null;
    }>;
  };
  if (!data?.category || !data.rows?.length) return null;

  // Show the universe name when available (e.g. "RTD Hydration"), otherwise the L2.
  const displayCategory = data.universe_name ?? data.category;

  const own = data.own_brand?.toLowerCase() ?? "";
  const curMap = new Map<
    string,
    { overall: number | null; ad: number | null; organic: number | null }
  >();
  const prevMap = new Map<
    string,
    { overall: number | null; ad: number | null; organic: number | null }
  >();
  for (const r of data.rows) {
    const m = r.period === "current" ? curMap : prevMap;
    m.set(r.brand, {
      overall: r.overall_sov,
      ad: r.ad_sov,
      organic: r.organic_sov,
    });
  }

  const brands: BrandSovRow[] = [...curMap.entries()]
    .map(([brand, cur], i) => {
      const prev = prevMap.get(brand);
      return {
        rank: i + 1,
        brand,
        own: brand.toLowerCase() === own,
        organic: cur.organic,
        ad: cur.ad,
        overall: cur.overall,
        prevOrganic: prev?.organic ?? null,
        prevAd: prev?.ad ?? null,
        prevOverall: prev?.overall ?? null,
      };
    })
    .sort((a, b) => (b.overall ?? 0) - (a.overall ?? 0))
    .map((r, i) => ({ ...r, rank: i + 1 }));

  const win = data.window;
  const compared = win && "previous_start" in win;

  const pp = (cur: number | null, prev: number | null): ReactNode => {
    if (cur === null || prev === null) return null;
    const raw = cur - prev;
    const rounded = Math.round(raw * 10) / 10;
    const sign = rounded > 0 ? "+" : "";
    const color =
      rounded > 0 ? "var(--up)" : rounded < 0 ? "var(--down)" : "var(--ink-3)";
    return (
      <span style={{ color, fontSize: "0.85em" }}>
        {sign}
        {rounded.toFixed(1)}pp
      </span>
    );
  };

  return (
    <TableCard
      title="Category SOV by brand"
      scope={periodLabel(win?.start, win?.end)}
      asof={`${displayCategory} · ${brands.length} brands`}
      cols={[
        { label: "#", type: "num" },
        { label: "Brand" },
        {
          label: "Organic SOV",
          type: "num",
          title:
            "SUM(organic_weight) / SUM(all_organic_weight) — organic SERP slots",
        },
        {
          label: "Ad SOV",
          type: "num",
          title: "SUM(ad_weight) / SUM(all_ad_weight) — sponsored SERP slots",
        },
        {
          label: "Overall SOV",
          type: "num",
          title:
            "SUM(total_weight) / SUM(all_total_weight) — combined rank-weighted",
        },
      ]}
      rows={brands.map((r) => ({
        key: r.brand,
        cells: [
          r.rank,
          <RowLabel
            key="b"
            name={
              <>
                {r.brand}
                {r.own ? (
                  <span className="tag" style={{ marginLeft: 6 }}>
                    you
                  </span>
                ) : null}
              </>
            }
          />,
          <MetricCell
            key="o"
            value={fmt(r.organic, "pct")}
            {...(compared ? { delta: pp(r.organic, r.prevOrganic) } : {})}
          />,
          <MetricCell
            key="a"
            value={fmt(r.ad, "pct")}
            {...(compared ? { delta: pp(r.ad, r.prevAd) } : {})}
          />,
          <MetricCell
            key="v"
            value={<b>{fmt(r.overall, "pct")}</b>}
            {...(compared ? { delta: pp(r.overall, r.prevOverall) } : {})}
          />,
        ],
      }))}
      foot={
        <>
          Every brand with Category SOV in <b>{displayCategory}</b>, sorted by
          Overall SOV. Same Category SOV source as the CEO &ldquo;Competitive
          position&rdquo; table &mdash; rank-weighted, pooled from the
          crawl&rsquo;s daily atoms.
          {compared ? (
            <> Change is the pp shift vs the comparison period.</>
          ) : null}
        </>
      }
    />
  );
}

/* ── 5. Timing & budget ──────────────────────────────────────────────────── */

function Timing({ customer, range }: { customer: string; range: DateRange }) {
  const q = useReport(OVERVIEW, customer, range);
  const win: MktWindow | undefined = q.data?.window;
  const days: MktDay[] = useMemo(
    () => (q.data?.series ?? []).map(dayOf),
    [q.data],
  );
  const cur = periodOf(q.data?.periods, "current");

  if (q.isLoading) return <State>Loading CPM pacing…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  const daily = days.map((d) => d.cpm);
  const real = daily.filter((v): v is number => v !== null);
  const last = real.length ? (real[real.length - 1] ?? null) : null;
  /**
   * The baseline a day is "dear" against is the POOLED period CPM —
   * SUM(spend)/SUM(impressions) over the window, which the server computes —
   * not the mean of the daily figures, which would let a 200-impression day
   * weigh as much as a 2-million one.
   */
  const pooled = cur?.cpm ?? null;
  const above = pooled === null ? null : real.filter((v) => v > pooled).length;
  const spread =
    real.length >= 2 ? Math.max(...real) - Math.min(...real) : null;

  /*
   * The running month-to-date average, now that the server sends per-day
   * impressions.
   *
   * It is a RATIO OF CUMULATIVE SUMS — cumulative spend over cumulative
   * impressions — not a running mean of the daily CPMs. Averaging the dailies
   * would let a 200-impression day weigh the same as a 2-million one, which is
   * the whole reason the figure is worth drawing. The line was left undrawn
   * before because the endpoint returned cpm but not the denominator behind
   * it, and a ratio cannot be pooled from other ratios.
   */
  const mtd: Array<number | null> = days.reduce<{
    spend: number;
    impr: number;
    out: Array<number | null>;
  }>(
    (acc, d) => {
      const spend = acc.spend + (d.spend ?? 0);
      const impr = acc.impr + (d.impressions ?? 0);
      return {
        spend,
        impr,
        out: [...acc.out, impr > 0 ? (spend / impr) * 1000 : null],
      };
    },
    { spend: 0, impr: 0, out: [] },
  ).out;
  const mtdLast = mtd.length ? (mtd[mtd.length - 1] ?? null) : null;

  const dayparts = (q.data?.dayparts ?? []).map(daypartOf);

  return (
    <>
      <PageBanner
        win={win}
        {...(q.data?.roas_basis?.note ? { roas: q.data.roas_basis.note } : {})}
      />

      <Card
        title="CPM pacing"
        scope={`selected period · ${win?.start ?? "—"} to ${win?.end ?? "—"}`}
        asof={`Data to ${win?.as_of ?? "—"}`}
        foot={
          <>
            <b>The published report draws two lines on one shared axis</b> —
            each day&rsquo;s own CPM against a running month-to-date average —
            and the shared axis is the whole point: drawn separately, a line
            moving ₹13 and a line moving ₹4 each fill their own height and look
            equally volatile. The running line is{" "}
            <code>cumulative SUM(spend) / cumulative SUM(impressions)</code> — a
            ratio of cumulative sums, not a running mean of the daily CPMs,
            which would let a 200-impression day weigh as much as a 2-million
            one. The daily line is the server&rsquo;s own{" "}
            <code>SUM(spend) / SUM(impressions) × 1000</code> per day, on the
            same principle. <i>Days above the period CPM</i> uses the pooled
            figure for the selected window, which the server does send, and the
            report&rsquo;s card is always month-to-date regardless of the period
            because that is the window a monthly cost budget is tracked against
            — here it follows the picker, which is the only window control the
            app has.
          </>
        }
      >
        <div className="panel-body">
          <div
            className="chart-stats"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 22,
            }}
          >
            <div className="chart-stat">
              <div className="kpi-label">MTD average CPM</div>
              <div className="chart-stat-val">{fmt(mtdLast, "price")}</div>
            </div>
            <div className="chart-stat">
              <div className="kpi-label">Latest day</div>
              <div
                className="chart-stat-val"
                style={
                  pooled !== null && last !== null
                    ? { color: last > pooled ? "var(--down)" : "var(--up)" }
                    : {}
                }
              >
                {fmt(last, "price")}
              </div>
            </div>
            <div className="chart-stat">
              <div className="kpi-label">Selected period</div>
              <div className="chart-stat-val">{fmt(pooled, "price")}</div>
            </div>
            <div className="chart-stat">
              <div className="kpi-label">Days above the period CPM</div>
              <div className="chart-stat-val">
                {above === null ? "—" : `${above} of ${real.length}`}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 4 }}>
            <DualChart
              dates={days.map((d) => d.day)}
              a={daily}
              b={mtd}
              kind="price"
            />
            <ChartLegend
              items={[
                {
                  label:
                    "Running month-to-date average — cumulative spend ÷ cumulative impressions",
                  solid: true,
                },
                {
                  label:
                    spread === null
                      ? "Daily CPM"
                      : `Daily CPM — swings ${fmt(spread, "price")} across the window`,
                  solid: false,
                },
              ]}
            />
          </div>
        </div>
      </Card>

      {/*
        Day-parting — eight bars, and they follow the picker.
        This card was empty on the strength of a footnote claiming the slot
        rollup "carries no date column at all". It does: blinkit_ads.
        snapshot_slot_delta has snapshot_date. Blinkit publishes no per-hour
        series, so these slots are the finest sub-daily grain that exists, but
        they do exist and they pool over any window.
      */}
      <Card
        title="Day-parting"
        scope={`selected period · ${win?.start ?? "—"} to ${win?.end ?? "—"}`}
        asof={`Data to ${win?.as_of ?? "—"}`}
        foot={
          <>
            Blinkit publishes <b>no per-hour series</b>, so these eight slots
            are the finest sub-daily grain there is &mdash; an hourly split
            genuinely cannot be measured, but the dayparts can. CPM per slot is{" "}
            <code>SUM(slot_spend) / SUM(slot_impressions) × 1000</code>, a ratio
            of sums over the selected days and never an average of daily CPMs;
            the bar is the slot&rsquo;s share of the period&rsquo;s slot spend,
            so the eight total 100%. A slot showing fewer days than the window
            has is one the crawl did not catch every day &mdash; its CPM is
            still sound, its spend is not the full period.
          </>
        }
      >
        <div className="panel-body">
          {dayparts.length === 0 ? (
            <div className="empty">
              No slot activity in {periodLabel(win?.start, win?.end)}.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {dayparts.map((p) => {
                const w = Math.max(0, Math.min(100, p.spend_share_pct ?? 0));
                return (
                  <div
                    key={p.slot_label}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "164px 1fr 92px 78px",
                      gap: 12,
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
                      {p.slot_label}
                    </span>
                    <div
                      style={{
                        background: "var(--track, #eef0f3)",
                        borderRadius: 4,
                        height: 16,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${w}%`,
                          height: "100%",
                          background: "var(--blue)",
                          borderRadius: 4,
                        }}
                        title={`${w.toFixed(1)}% of period slot spend`}
                      />
                    </div>
                    <span
                      className="num"
                      style={{ fontSize: 12.5, textAlign: "right" }}
                    >
                      {fmt(p.spend, "money")}
                    </span>
                    <span
                      className="num"
                      style={{
                        fontSize: 12.5,
                        textAlign: "right",
                        fontWeight: 600,
                      }}
                    >
                      {fmt(p.cpm, "price")}
                    </span>
                  </div>
                );
              })}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "164px 1fr 92px 78px",
                  gap: 12,
                  fontSize: 11,
                  color: "var(--ink-3)",
                  paddingTop: 2,
                }}
              >
                <span>Slot</span>
                <span>Share of slot spend</span>
                <span style={{ textAlign: "right" }}>Spend</span>
                <span style={{ textAlign: "right" }}>CPM</span>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/*
        Budget health. Genuinely a FIXED WINDOW: the alert feed is a live
        snapshot of the latest crawl and ignores any date range it is given, so
        it can never follow the picker. The three rows and their wording are the
        report's own static legend; only the counts are missing.
      */}
      <Card
        title="Budget health"
        fixedWindow
        asof={`Live status · page runs to ${win?.as_of ?? "—"}`}
        foot={
          <>
            <b>Read this next to day-parting.</b> On the published report these
            three counts sit beside the slot chart, because a search campaign
            that spends out before the day ends has its budget gone before
            morning demand arrives — a pacing outcome happening by default, not
            a decision. The counts come from the campaign alert feed, which is a{" "}
            <b>live snapshot with no window at all</b>: it ignores the dates it
            is given, which is why this card is tagged <i>fixed window</i> and
            can never follow the picker. No endpoint serves that feed yet, so
            the rows read &ldquo;—&rdquo;. The ON_HOLD wording is the
            platform&rsquo;s own: those campaigns are still serving and still
            billing. The three rows overlap by construction and{" "}
            <b>must not be added</b>.
          </>
        }
      >
        <div className="panel-body">
          <div className="stat-list">
            {[
              {
                label: "Campaigns exhausting their daily budget",
                severity: "high",
                sub: ALERT_LEGEND.DAILY_BUDGET_EXHAUST,
              },
              {
                label: "Currently ON_HOLD",
                severity: "med",
                sub: ALERT_LEGEND.ON_HOLD,
              },
              {
                label: "Campaigns exhausting their overall budget",
                severity: "low",
                sub: ALERT_LEGEND.OVERALL_BUDGET_EXHAUST,
              },
            ].map((r) => (
              <div className="stat-row" key={r.label}>
                <span className={`stat-swatch severity-${r.severity}`} />
                <div className="stat-body">
                  <div className="stat-label">{r.label}</div>
                  <div className="stat-sub">{r.sub}</div>
                </div>
                <div className="stat-val muted">—</div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </>
  );
}

/* ── 6. SKUs ─────────────────────────────────────────────────────────────── */

const SKU_COLS: Col[] = [
  { label: "SKU" },
  {
    label: "Planned spend",
    type: "num",
    title: "Pro-rated planned spend from the SKU plan for the selected window",
  },
  {
    label: "Assoc. spend",
    type: "num",
    title:
      "associated_spend — the whole campaign's spend stamped on this product. Not additive.",
  },
  { label: "Assoc. sales", type: "num" },
  { label: "Assoc. ROAS", type: "num" },
  { label: "Units", type: "num" },
  { label: "CVR", type: "num" },
  { label: "CPM", type: "num" },
];

function skuCells(r: ProductRow, dim?: boolean): ReactNode[] {
  return [
    <RowLabel
      name={
        <span {...(dim ? { style: { color: "var(--ink-3)" } } : {})}>
          {r.sku}
        </span>
      }
      sub={
        <>
          {[
            r.grammage,
            r.outside_catalogue === true ? "outside the single-SKU set" : null,
          ]
            .filter((x): x is string => typeof x === "string" && x !== "")
            .join(" · ")}
          {r.grammage || r.outside_catalogue === true ? " · " : null}
          product_id <span className="rowlabel-link">{r.product_id}</span>
        </>
      }
    />,
    fmt(r.planned_spend, "money"),
    fmt(r.spend, "money"),
    fmt(r.ad_sales, "money"),
    <Roas v={r.roas} />,
    fmt(r.units, "int"),
    fmt(r.cvr, "pct"),
    fmt(r.cpm, "price"),
  ];
}

function Skus({ customer, range }: { customer: string; range: DateRange }) {
  const ov = useReport(OVERVIEW, customer, range);
  const q = useReport<ProductRow>(
    "/v1/reports/marketing/products",
    customer,
    range,
    { limit: 500 },
  );
  const [drill, setDrill] = useState(false);
  const win: MktWindow | undefined = ov.data?.window;
  const cur = periodOf(ov.data?.periods, "current");

  if (q.isLoading) return <State>Loading SKUs…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <>
        <PageBanner
          win={win}
          {...(ov.data?.roas_basis?.note
            ? { roas: ov.data.roas_basis.note }
            : {})}
        />
        <State>No SKU-level ad spend in this window.</State>
      </>
    );
  }

  /**
   * Singles against bundles.
   *
   * The published report partitions on membership of a hand-maintained 16-SKU
   * set. Here the crawl does it: a product the crawl does not know is a virtual
   * bundle or a delisted listing, which is the same partition drawn from live
   * data rather than a frozen literal. Bundles stay out of the table and are
   * NAMED in the footnote — their ad spend is real money inside the campaign
   * totals above, so dropping them silently would make the cards disagree.
   */
  const singles = rows.filter((r) => r.outside_catalogue !== true);
  const bundles = rows.filter((r) => r.outside_catalogue === true);
  const top = [...singles]
    .sort((a, b) => (b.spend ?? 0) - (a.spend ?? 0))
    .slice(0, 5);
  const assocTotal = rows.reduce((a, r) => a + (r.spend ?? 0), 0);
  const trueSpend = cur?.spend ?? null;
  // Two products in one campaign carry the same stamped figures. Counted so the
  // drill can say why identical rows are not duplicate data.
  const dupes = rows.filter((r) =>
    rows.some(
      (o) => o !== r && o.spend === r.spend && o.ad_sales === r.ad_sales,
    ),
  ).length;
  const ordered = [...rows].sort(
    (a, b) =>
      Number(a.outside_catalogue === true) -
        Number(b.outside_catalogue === true) || (b.spend ?? 0) - (a.spend ?? 0),
  );

  const foot = (
    <>
      {top.length === singles.length
        ? `All ${top.length}`
        : `Top ${top.length}`}{" "}
      of {singles.length} single SKUs by associated spend, joined on{" "}
      <code>product_id</code> to the same crawl catalogue the Operations and CEO
      pages read. These are <b>associated</b> figures — a campaign&rsquo;s whole
      total stamped onto every product it targets, so SKUs sharing a campaign
      show identical numbers and <b>the column must never be summed</b>: the{" "}
      {rows.length} rows here add to {fmt(assocTotal, "money")} against a true
      spend of {fmt(trueSpend, "money")} for the same window
      {trueSpend ? ` — ${(assocTotal / trueSpend).toFixed(1)}× if summed` : ""}.
      Per-SKU attribution only exists for dedicated single-SKU × single-city
      campaigns.{" "}
      {bundles.length ? (
        <>
          <b>
            {bundles.length} listing{bundles.length > 1 ? "s" : ""} the crawl
            does not know
          </b>{" "}
          ({bundles.map((b) => b.sku).join(", ")}) carr
          {bundles.length > 1 ? "y" : "ies"} associated spend too — virtual
          bundles are excluded everywhere until the bundle table ships, and
          their ad spend is still real money inside the campaign totals
          above.{" "}
        </>
      ) : null}
      {q.data?.basis_note} <b>Matches the selected period</b> — unlike the
      published report, whose SKU table is a frozen window because the
      associated measures are refused under a group-by.
    </>
  );

  return (
    <>
      <PageBanner
        win={win}
        {...(ov.data?.roas_basis?.note
          ? { roas: ov.data.roas_basis.note }
          : {})}
      />
      <TableCard
        title="By SKU"
        scope={`${win?.start ?? "—"} to ${win?.end ?? "—"}`}
        asof={`Data to ${win?.as_of ?? "—"}`}
        actions={
          <ViewAll
            n={rows.length}
            onClick={() => {
              setDrill(true);
            }}
          />
        }
        cols={SKU_COLS}
        rows={top.map((r) => ({ key: r.product_id, cells: skuCells(r) }))}
        foot={foot}
      />
      {drill ? (
        <Drill
          title="All SKUs"
          count={`${rows.length} SKUs · ${win?.start ?? "—"} to ${win?.end ?? "—"}`}
          onClose={() => {
            setDrill(false);
          }}
          foot={
            <>
              <span>
                Every figure is <b>associated</b> — a campaign&rsquo;s entire
                total stamped onto each product it targets. Read rows on their
                own; never add this column up.
                {dupes
                  ? ` ${dupes} rows share identical figures because they share a campaign — that is the stamping, not duplicate data.`
                  : ""}
              </span>
              <span className="spacer" />
              <span>Data to {win?.as_of ?? "—"}</span>
            </>
          }
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {SKU_COLS.map((c, i) => (
                    <th
                      key={c.label}
                      {...(i === 0 ? {} : c.type ? { className: c.type } : {})}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ordered.map((r) => (
                  <tr key={r.product_id}>
                    {skuCells(r, r.outside_catalogue === true).map(
                      (cell, i) => (
                        <td
                          key={SKU_COLS[i]?.label ?? i}
                          {...(i === 0
                            ? {}
                            : SKU_COLS[i]?.type
                              ? { className: SKU_COLS[i]?.type }
                              : {})}
                        >
                          {cell}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Drill>
      ) : null}
    </>
  );
}
